export type AgingBucket = 'current' | '1-7' | '8-14' | '15-21' | '22+';

/**
 * Today's local date (YYYY-MM-DD) used by aging / overdue math. It was pinned
 * to the mock data's range before the app was backed by the real API.
 */
export const REFERENCE_TODAY = localToday();

function localToday(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(dateIso: string, days: number): string {
  const date = new Date(`${dateIso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.floor((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000);
}

export function agingBucket(dueDate: string, today = REFERENCE_TODAY): AgingBucket {
  const daysPastDue = daysBetween(dueDate, today);
  if (daysPastDue <= 0) return 'current';
  if (daysPastDue <= 7) return '1-7';
  if (daysPastDue <= 14) return '8-14';
  if (daysPastDue <= 21) return '15-21';
  return '22+';
}

/** Buckets used by 30-day-wide reports (e.g. Debt Aging), keyed off the due date. */
export type DebtAgingBucket = 'future' | 'current' | '1-30' | '31-60' | '61-90' | '91+';

export function debtAgingBucket(dueDate: string, orderDate: string, asOf = REFERENCE_TODAY): DebtAgingBucket {
  if (orderDate > asOf) return 'future';
  const daysPastDue = daysBetween(dueDate, asOf);
  if (daysPastDue <= 0) return 'current';
  if (daysPastDue <= 30) return '1-30';
  if (daysPastDue <= 60) return '31-60';
  if (daysPastDue <= 90) return '61-90';
  return '91+';
}

/** Weekly buckets used by Unpaid Sales Aging, keyed off the requested/order date rather than due date. */
export type WeeklyAgingBucket = 'future' | '0-7' | '8-14' | '15-21' | '22-28' | '29-35' | '36-42' | '43-49' | '50-56' | '57+';

export function weeklyAgingBucket(orderDate: string, asOf = REFERENCE_TODAY): WeeklyAgingBucket {
  if (orderDate > asOf) return 'future';
  const daysOld = daysBetween(orderDate, asOf);
  if (daysOld <= 7) return '0-7';
  if (daysOld <= 14) return '8-14';
  if (daysOld <= 21) return '15-21';
  if (daysOld <= 28) return '22-28';
  if (daysOld <= 35) return '29-35';
  if (daysOld <= 42) return '36-42';
  if (daysOld <= 49) return '43-49';
  if (daysOld <= 56) return '50-56';
  return '57+';
}
